// Ошибки клиента API пульта (задача D4). Три вида, и по каждому экран знает, что сказать человеку:
// - `ApiError` — сервис ответил ошибкой в форме контракта `{ error: { code, messageRu } }`: текст — от сервера;
// - `ServiceUnavailableError` — сервис не ответил: сеть, 502/503/504 от прокси, тело не JSON (nginx отдал свою
//   страницу). Пульт показывает «Сервис Анны не отвечает» и данные, которые уже есть;
// - `ContractError` — ответ пришёл, но не прошёл схему контракта: пульт и сервер разных версий.
import type { z } from 'zod';

export const SERVICE_UNAVAILABLE_RU = 'Сервис Анны не отвечает';

export const CONTRACT_ERROR_RU = 'Ответ сервера не того формата — обновите страницу. Если не поможет, сообщите Владу.';

const UNEXPECTED_RU = 'Непредвиденная ошибка пульта.';

/** Сервис ответил ошибкой: HTTP-статус, код для кода пульта и текст для человека. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly messageRu: string;

  constructor(status: number, code: string, messageRu: string) {
    super(messageRu);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.messageRu = messageRu;
  }
}

/** Сервис не ответил. `status` — HTTP-статус ответа прокси (502…); `null` — ответа не было совсем (обрыв сети). */
export class ServiceUnavailableError extends Error {
  readonly messageRu = SERVICE_UNAVAILABLE_RU;
  readonly status: number | null;

  constructor(o: { readonly status: number | null; readonly cause?: unknown }) {
    super(SERVICE_UNAVAILABLE_RU, { cause: o.cause });
    this.name = 'ServiceUnavailableError';
    this.status = o.status;
  }
}

/** Ответ не прошёл схему контракта. `path` — какой запрос, `issues` — что не сошлось (для журнала и отладки). */
export class ContractError extends Error {
  readonly messageRu = CONTRACT_ERROR_RU;
  readonly path: string;
  readonly issues: readonly z.core.$ZodIssue[];

  constructor(o: { readonly path: string; readonly issues: readonly z.core.$ZodIssue[] }) {
    super(`${CONTRACT_ERROR_RU} Запрос: ${o.path}.`);
    this.name = 'ContractError';
    this.path = o.path;
    this.issues = o.issues;
  }
}

/** Текст ошибки для человека: у ошибок клиента — их `messageRu`, у чужой — её сообщение. */
export function messageRuOf(error: unknown): string {
  if (error instanceof ApiError || error instanceof ServiceUnavailableError || error instanceof ContractError) {
    return error.messageRu;
  }
  if (error instanceof Error && error.message.trim() !== '') return error.message;
  return UNEXPECTED_RU;
}

/**
 * Ленивый кусок сборки не загрузился: вышла новая версия пульта, а вкладка просит файлы старой. Тексты браузеров:
 * Chrome — «Failed to fetch dynamically imported module», Firefox — «error loading dynamically imported module»,
 * Safari — «Importing a module script failed.»; Vite не смог догрузить стили куска — «Unable to preload CSS».
 */
const CHUNK_LOAD_MESSAGE =
  /dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_LOAD_MESSAGE.test(error.message);
}
